import { useTranslation } from 'react-i18next';
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import type { DashboardDistribution } from '@/api/dashboard';
import { Eyebrow } from '@/components/ui/Eyebrow';
import { StatusDot } from '@/components/ui/StatusDot';
import { Table, type Column } from '@/components/ui/Table';
import { type } from '@/theme/typography';

import {
  NO_ANIMATION,
  SCORES_AXIS_HEIGHT,
  SCORES_CHART_HEIGHT,
  SCORES_LABEL_MARGIN,
  axisLine,
  axisTick,
  barFill,
  barLabel,
  modalColumnFill,
  scoresDomainMax,
  verdictFill,
} from './chartStyle';

interface Props {
  distribution: DashboardDistribution;
}

type VerdictRow = DashboardDistribution['verdicts'][number];
type ScoreRow = DashboardDistribution['score_histogram'][number];

/**
 * `DESIGN.md` §6.2, Section 4 — Feedback distribution.
 *
 * ⚠️ **Blinded and viewer-dependent, and the screen computes nothing** (ADR 0006, ADR 0015 §3).
 * Both distributions arrive already filtered through the same blind gate the interview endpoints
 * use, so a caller who still owes feedback for a candidate has that candidate's ratings and verdict
 * excluded from what they see here. Deriving anything further on the client — a total, a mean, a
 * "who scores harshly" cut — would compute over a set the client cannot reason about and would put
 * back the attribution ADR 0015 §1 forbids. This component renders what it is given.
 *
 * Every bucket 1–5 is present even at zero, and **unanswered scores are excluded by the API** — a
 * skipped question is information, not a sixth bar, and nothing here may invent one.
 *
 * §9: each chart is a `figure` with an `aria-label` summarising its series, the SVG itself is
 * hidden from assistive technology, and the numbers are repeated in a visually hidden table. "A bar
 * chart is invisible to a screen reader."
 */
export function FeedbackDistributionSection({ distribution }: Props) {
  const { t } = useTranslation(['dashboard', 'components']);

  const verdicts = distribution.verdicts;
  const scores = distribution.score_histogram;
  /**
   * A predicate, not a total — the docblock above forbids deriving one and this is the line that
   * would have broken it. It summed the two series into a single number, and that number means
   * nothing even as a number: one submission contributes one verdict and several scores, so the two
   * are different populations and their sum counts neither. Asking each bucket whether it is empty
   * answers the only question this needs answered, and derives nothing.
   */
  const nothingSubmitted =
    verdicts.every((verdict) => verdict.count === 0) && scores.every((score) => score.count === 0);

  /**
   * ⚠️ **Nothing submitted in this period is one state, and it is the sentence — not the sentence
   * with a grid of zeros drawn under it.**
   *
   * This rendered the empty line *and* both charts at zero (found in a live browser, 2026-07-25),
   * which is precisely the shape §6.2 opens by forbidding — *"A brand-new tenant must not see a grid
   * of zeros"* — and §8's *"different states, different sentences"* arriving one section below the
   * dashboard-wide setup path that was built for the same rule. It is not a first-run-only case: any
   * period with no submissions reaches it.
   *
   * The collapse is §6.2's own answer for Section 1 at zero (*"the whole section collapses to one
   * line"*), and the charts carry nothing else here — an all-zero bar chart states no fact the
   * sentence has not already stated, and its hidden table would read four verdicts and five scores
   * of zero to a screen reader for the same nothing.
   *
   * **Section 3 is deliberately not treated this way**: §6.2 requires all five statuses present and
   * *"a zero renders as `0`"*, so its zeros are the design and not a grid of them.
   */
  if (nothingSubmitted) {
    return (
      <section className="mt-10" aria-label={t('dashboard:distribution.title')}>
        <Eyebrow>{t('dashboard:distribution.title')}</Eyebrow>
        <p className={`${type.metadata} -mt-2`}>{t('dashboard:distribution.empty')}</p>
      </section>
    );
  }

  /**
   * The bucket the accent goes on: the one with the **highest count** — the dataset's modal value —
   * read from the counts the API already sent (§6.2 Section 4, 2026-08-12, TASK-154).
   *
   * ⚠️ **This is an ordering, not a derived value.** It computes no total, no mean and no cut, which is
   * the line ADR 0006 and ADR 0015 §3 draw around this section: the client renders what it is given and
   * may sort it, because sorting reveals nothing the five printed counts do not already say.
   *
   * ⚠️ **On a tie, no column is accented** (§6.2's own rule). The accent's whole claim is *"this bucket
   * is the one"*, and with two equal tallest columns there is no such bucket, so accenting the
   * left-hand one would be an arbitrary choice wearing the brand colour. The all-zero case never
   * reaches this line — the section's empty state returns above — and the tie rule would accent nothing
   * in that data anyway, which is the two rules agreeing rather than one covering for the other.
   *
   * It is a **rule over the data and never an index**: the reference highlights `3` because `3` is that
   * screenshot's modal value, and §6.2 deliberately does not record the number.
   */
  const highestCount = Math.max(...scores.map((score) => score.count));
  const modalBuckets = scores.filter((score) => score.count === highestCount);
  const accentedScore = modalBuckets.length === 1 ? modalBuckets[0].score : null;

  const verdictData = verdicts.map((verdict) => ({
    ...verdict,
    label: t(`components:recommendation.${verdict.verdict}`).toUpperCase(),
  }));

  const verdictColumns: Array<Column<VerdictRow>> = [
    {
      key: 'verdict',
      header: t('dashboard:distribution.recommendation'),
      primary: true,
      // Nothing narrows this value, here or at the API edge: `VerdictCount.verdict` is the declared
      // `OverallRecommendation` in the document since backend TASK-104, so `row.verdict` is one of
      // the four by type. It reached `StatusDot` through an `as` cast until then — where
      // `styles[value]` is `undefined` and `style.pill` throws, taking the whole dashboard down.
      cell: (row) => <StatusDot value={row.verdict} />,
    },
    {
      key: 'count',
      header: t('dashboard:distribution.count'),
      align: 'right',
      cell: (row) => <span className={type.numeral}>{row.count}</span>,
    },
  ];

  const scoreColumns: Array<Column<ScoreRow>> = [
    {
      key: 'score',
      header: t('dashboard:distribution.score'),
      primary: true,
      cell: (row) => <span className={type.numeral}>{row.score}</span>,
    },
    {
      key: 'count',
      header: t('dashboard:distribution.count'),
      align: 'right',
      cell: (row) => <span className={type.numeral}>{row.count}</span>,
    },
  ];

  const verdictSummary = verdictData
    .map((verdict) => `${verdict.label}: ${verdict.count}`)
    .join(', ');
  const scoreSummary = scores.map((score) => `${score.score}: ${score.count}`).join(', ');

  return (
    <section className="mt-10" aria-label={t('dashboard:distribution.title')}>
      <Eyebrow>{t('dashboard:distribution.title')}</Eyebrow>

      <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
        <figure
          aria-label={t('dashboard:distribution.recommendationsSummary', {
            summary: verdictSummary,
          })}
        >
          {/*
            §6.2: the two sub-labels are set in **eyebrow** type, not at *Section heading* — they are
            the reference's own setting, and they are labels inside a section that already has an
            eyebrow of its own.
          */}
          <figcaption className={type.eyebrow}>
            {t('dashboard:distribution.recommendations')}
          </figcaption>
          <div aria-hidden className="mt-3">
            <ResponsiveContainer width="100%" height={160}>
              <BarChart layout="vertical" data={verdictData} margin={{ right: 28 }}>
                <XAxis type="number" hide />
                <YAxis
                  type="category"
                  dataKey="label"
                  width={96}
                  tick={axisTick}
                  axisLine={axisLine}
                  tickLine={false}
                />
                <Bar dataKey="count" barSize={12} isAnimationActive={NO_ANIMATION}>
                  {verdictData.map((verdict) => (
                    <Cell key={verdict.verdict} fill={verdictFill[verdict.verdict]} />
                  ))}
                  <LabelList dataKey="count" position="right" {...barLabel} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="sr-only">
            {/*
             * `stacked={false}`: an `sr-only` table has no width, so §10's stacking — a viewport
             * affordance for a table someone is looking at — would only make what a screen reader
             * hears depend on the browser window. It keeps its real header row at every width.
             */}
            <Table
              columns={verdictColumns}
              rows={verdicts}
              rowKey={(row) => row.verdict}
              stacked={false}
            />
          </div>
        </figure>

        <figure aria-label={t('dashboard:distribution.scoresSummary', { summary: scoreSummary })}>
          <figcaption className={type.eyebrow}>{t('dashboard:distribution.scores')}</figcaption>
          <div aria-hidden className="mt-3">
            {/*
              ⚠️ **The height is derived from §6.2's ≈ 83 px column rather than chosen** (2026-08-12,
              after `ui-reviewer` measured 56.3 px): it is the plot area, plus the room the count
              labels take above it (the top margin), plus the axis band beneath it. The Y domain is
              pinned in the same breath, because at `height={140}` the plot area was already 90 px
              and the column still drew 56.3 px — `chartStyle.ts` carries the arithmetic.
            */}
            <ResponsiveContainer width="100%" height={SCORES_CHART_HEIGHT}>
              {/*
                ⚠️ **Vertical columns, the count above each, and the modal column in the accent** — both
                halves of TASK-129's reversal `reversed` again (§6.2, 2026-08-12, TASK-148/TASK-154, with
                TASK-127..129's arguments known: they were put to the user and the user chose the original
                design).

                 * **Two idioms are the point rather than an oversight.** *Recommendations* compares four
                   named categories, where a long label sits beside its own bar; *Scores* is an **ordered
                   1–5 scale**, which reads left to right with height as the value. What TASK-129 found
                   encoded nothing does encode something once the second chart is the scale rather than a
                   second set of categories.
                 * **The accent is answered rather than ignored.** The count is printed **above every
                   column**, so the accent is no longer what tells a reader which bucket is largest and
                   cannot be doing the arithmetic on their behalf; and it is the **tenant's** colour, the
                   same fill the selected period segment wears in the header, where it means *"this one"*.

                ⚠️ **`width={24}` on the old horizontal axis was measured and is not reusable here**: a
                vertical chart has different axis geometry, so the category axis is the X axis and its
                labels sit under their own columns rather than at a width copied from anywhere.
              */}
              <BarChart
                data={scores}
                margin={{ top: SCORES_LABEL_MARGIN, right: 0, bottom: 0, left: 0 }}
              >
                <XAxis
                  type="category"
                  dataKey="score"
                  height={SCORES_AXIS_HEIGHT}
                  tick={axisTick}
                  axisLine={axisLine}
                  tickLine={false}
                  interval={0}
                />
                <YAxis type="number" hide domain={[0, scoresDomainMax]} />
                <Bar dataKey="count" barSize={85} isAnimationActive={NO_ANIMATION}>
                  {scores.map((score) => (
                    <Cell
                      key={score.score}
                      fill={score.score === accentedScore ? modalColumnFill : barFill}
                    />
                  ))}
                  <LabelList dataKey="count" position="top" {...barLabel} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="sr-only">
            {/* Not stacked, for the reason given on the recommendations table above. */}
            <Table
              columns={scoreColumns}
              rows={scores}
              rowKey={(row) => row.score}
              stacked={false}
            />
          </div>
        </figure>
      </div>
    </section>
  );
}
